// Local barcode rendering for printed documents.
//
// Barcodes are rendered here, in the CMS bundle, *before* a print document is
// opened — never by a script fetched at print time. A value that cannot be
// encoded is reported back so the job can be blocked with the exact item named.

import JsBarcode from "jsbarcode";

const SVG_NS = "http://www.w3.org/2000/svg";

export const getDefaultDocument = () => (typeof document !== "undefined" ? document : undefined);

// The values the label sheet has always used (see the label template CSS).
export const LABEL_BARCODE_OPTIONS = Object.freeze({
  format: "CODE128",
  width: 0.92,
  height: 21,
  displayValue: false,
  margin: 0,
  background: "transparent",
});

// Barcode density, judged by the printed width of one bar ("module", X):
//   healthy  X ≥ 0.19 mm  (7.5 mil: ordinary retail scanning)
//   warning  0.13 ≤ X < 0.19 mm  (scans with good scanners; test one label first)
//   unsafe   X < 0.13 mm  (below 5 mil: at 300 dpi a bar is ~1.5 printer dots
//            and widths round unevenly; many handheld scanners fail)
// The thresholds are fixed so the same barcode on the same preset always gets
// the same verdict. On the standard 3 × 7 sheet the shop's variant barcodes
// (16–26 characters) are healthy or, for the longest, a warning — never unsafe.
export const BARCODE_DENSITY = Object.freeze({ HEALTHY: "healthy", WARNING: "warning", UNSAFE: "unsafe" });
export const HEALTHY_MODULE_WIDTH_MM = 0.19;
export const UNSAFE_MODULE_WIDTH_MM = 0.13;

export const classifyBarcodeDensity = (moduleWidthMM) => {
  if (!(moduleWidthMM > 0) || moduleWidthMM < UNSAFE_MODULE_WIDTH_MM) return BARCODE_DENSITY.UNSAFE;
  if (moduleWidthMM < HEALTHY_MODULE_WIDTH_MM) return BARCODE_DENSITY.WARNING;
  return BARCODE_DENSITY.HEALTHY;
};

const serializeSvg = (svg) => {
  if (typeof XMLSerializer !== "undefined") {
    return new XMLSerializer().serializeToString(svg);
  }
  return svg.outerHTML;
};

/**
 * Renders one barcode to an SVG string.
 *
 * Returns `{ ok: true, svg, moduleCount }` or
 * `{ ok: false, reason: "empty" | "unencodable", message }`.
 */
export const renderBarcodeSvg = (value, options = {}, doc = getDefaultDocument()) => {
  const text = String(value ?? "").trim();
  if (!text) {
    return { ok: false, reason: "empty", message: "Barcode is empty." };
  }

  const settings = { ...LABEL_BARCODE_OPTIONS, ...options };
  const svg = doc.createElementNS(SVG_NS, "svg");

  try {
    JsBarcode(svg, text, settings);
  } catch (error) {
    // JsBarcode throws a plain string for invalid input.
    return {
      ok: false,
      reason: "unencodable",
      message: typeof error === "string" ? error : error?.message || "Barcode cannot be encoded.",
    };
  }

  const widthPx = parseFloat(svg.getAttribute("width"));
  if (!svg.childNodes.length || !Number.isFinite(widthPx) || widthPx <= 0) {
    return { ok: false, reason: "unencodable", message: "Barcode rendered no bars." };
  }

  return {
    ok: true,
    svg: serializeSvg(svg),
    // With margin 0 the drawing is exactly modules × bar width wide.
    moduleCount: Math.round(widthPx / settings.width),
  };
};

/** Renders each distinct value once. Returns Map<value, result>. */
export const renderBarcodeSet = (values, options = {}, doc = getDefaultDocument()) => {
  const results = new Map();
  (values || []).forEach((value) => {
    const key = String(value ?? "").trim();
    if (!results.has(key)) {
      results.set(key, renderBarcodeSvg(key, options, doc));
    }
  });
  return results;
};

/** Physical width of one bar when the barcode is stretched across `drawableWidthMM`. */
export const estimateModuleWidthMM = (moduleCount, drawableWidthMM) =>
  moduleCount > 0 && drawableWidthMM > 0 ? drawableWidthMM / moduleCount : 0;
